/**
 * Bestsellers.jsx — EVORA Bestsellers Page
 * 
 * Displays top-selling products sorted by salesCount.
 * Follows the same patterns as NewArrivals for consistency.
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/common/Layout/Layout';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { useToast } from '../../context/ToastContext';
import { BRAND } from '../../config/brand';
import './Bestsellers.css';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const API_URL  = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const LIMIT    = 50;
const SKELETONS = 8;
const BRAND_NAME = BRAND?.name || 'EVORA';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Normalise product ID once (stable pid, no random — hydration-safe) */
function normaliseProducts(raw) {
  return raw.map((p, i) => ({ ...p, pid: p._id ?? p.id ?? `list-${i}` }));
}

// CollectionPage + BreadcrumbList built in component (canonical + category-aware)

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SkeletonGrid() {
  return (
    <div className="bs-grid" aria-busy="true" aria-label="Loading products">
      {Array.from({ length: SKELETONS }).map((_, i) => (
        <div key={`skel-${i}`} className="bs-skeleton" aria-hidden="true">
          <div className="bs-skeleton__img shimmer" />
          <div className="bs-skeleton__body">
            <div className="bs-skeleton__line shimmer bs-skeleton__line--name" />
            <div className="bs-skeleton__line shimmer bs-skeleton__line--sub" />
            <div className="bs-skeleton__line shimmer bs-skeleton__line--price" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="bs-error" role="alert">
      <svg
        className="bs-error__icon"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8"  x2="12"    y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p className="bs-error__msg">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="bs-btn bs-btn--primary"
      >
        Try Again
      </button>
    </div>
  );
}

// Internal links use preferred paths /men, /women (crawlability, sitemap alignment)
function EmptyState({ category }) {
  const primaryLink = category === 'women' ? '/women' : '/men';
  const ghostLink = category === 'women' ? '/men' : '/women';
  const primaryLabel = category === 'women' ? 'Shop Women' : 'Shop Men';
  const ghostLabel = category === 'women' ? 'Shop Men' : 'Shop Women';
  return (
    <div className="bs-empty" role="status">
      <div className="bs-empty__icon" aria-hidden="true">⭐</div>
      <p className="bs-empty__title">No bestsellers available</p>
      <p className="bs-empty__sub">
        Check back soon for our top-rated products, or explore our full collection.
      </p>
      <div className="bs-empty__ctas">
        <Link to={primaryLink} className="bs-btn bs-btn--primary">{primaryLabel}</Link>
        <Link to={ghostLink} className="bs-btn bs-btn--ghost">{ghostLabel}</Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bestsellers — Page component
// ─────────────────────────────────────────────────────────────────────────────

function Bestsellers() {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category')?.trim() || null; // e.g. 'men' | 'women'

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  // fetchKey increment triggers a clean re-fetch via useEffect
  const [fetchKey, setFetchKey] = useState(0);

  const { showToast } = useToast();

  // Stable ref for showToast; avoids infinite fetch loop
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  // AbortController created at useEffect scope, not inside callback
  useEffect(() => {
    const controller = new AbortController();
    const MAX_RETRIES = 2;
    const TIMEOUT_MS  = 30_000; // Allow for backend cold start
    let timeoutId;
    let retryTimeoutId;

    async function run(attempt = 0) {
      timeoutId = setTimeout(() => controller.abort('timeout'), TIMEOUT_MS);

      try {
        const params = new URLSearchParams({ limit: String(LIMIT) });
        if (category) params.set('category', category);
        const res = await fetch(
          `${API_URL}/products/best-sellers?${params}`,
          { signal: controller.signal, cache: 'no-store' },
        );
        clearTimeout(timeoutId);

        if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });

        const data = await res.json();

        if (data.success && Array.isArray(data.products)) {
          // Normalise IDs before setting state
          setProducts(normaliseProducts(data.products));
          setError(null);
        } else {
          setProducts([]);
        }
        // setLoading only here (success path), not in finally
        setLoading(false);

      } catch (err) {
        clearTimeout(timeoutId);
        if (controller.signal.aborted && err.message !== 'timeout') return; // unmounted

        const isTimeout = err.message === 'timeout' || err.name === 'AbortError';

        if (!isTimeout && attempt < MAX_RETRIES) {
          // loading stays true during retry delay
          const delay = Math.min(1_000 * 2 ** attempt, 8_000);
          retryTimeoutId = setTimeout(() => run(attempt + 1), delay);
          return;
        }

        const msg = isTimeout
          ? 'Request timed out. Please check your connection.'
          : 'Failed to load bestsellers. Please try again.';

        setError(msg);
        setProducts([]);
        // setLoading(false) only after all retries exhausted
        setLoading(false);
        showToastRef.current(msg, 'error');
      }
    }

    run();

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
    };
  }, [fetchKey, category]);

  // handleRetry just increments the key, triggering useEffect
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    setFetchKey((k) => k + 1);
  }, []);

  const handleAddToCart = useCallback((productName) => {
    showToastRef.current(`${productName} added to your bag — check it out when you're ready.`, 'success');
  }, []);

  // Page URLs and labels (for meta + schema)
  const pageTitle = category
    ? `Bestsellers — ${category === 'men' ? "Men's" : "Women's"} | ${BRAND_NAME}`
    : `Bestsellers | ${BRAND_NAME}`;
  const canonicalUrl = category
    ? `${SITE_URL}/bestsellers?category=${encodeURIComponent(category)}`
    : `${SITE_URL}/bestsellers`;
  const headerLabel = category
    ? `Bestsellers — ${category === 'men' ? "Men's" : "Women's"}`
    : 'Bestsellers';

  // Standalone BreadcrumbList (Google recommends separate from CollectionPage)
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Bestsellers', item: `${SITE_URL}/bestsellers` },
      ...(category ? [{
        '@type': 'ListItem',
        position: 3,
        name: category === 'men' ? "Men's" : "Women's",
        item: canonicalUrl,
      }] : []),
    ],
  }), [category, canonicalUrl]);

  // CollectionPage: mainEntity (ItemList) only when loaded to avoid schema flicker
  const collectionSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: category
      ? `Shop ${category === 'men' ? "men's" : "women's"} bestsellers from ${BRAND_NAME}. Top-rated premium essentials. Free shipping on all orders.`
      : `Shop bestsellers from ${BRAND_NAME}. Top-rated premium essentials loved by thousands. Free shipping on all orders.`,
    url: canonicalUrl,
    provider: { '@type': 'Organization', name: BRAND_NAME, url: SITE_URL },
    ...(!loading && products.length > 0 && {
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: products.length,
        itemListElement: products.slice(0, 20).map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: p.name,
          url: `${SITE_URL}/product/${p.slug ?? p.pid}`,
          image: p.images?.[0]?.url ?? p.image,
        })),
      },
    }),
  }), [pageTitle, canonicalUrl, category, loading, products]);

  const pageDescription = category
    ? `Shop ${category === 'men' ? "men's" : "women's"} bestsellers from ${BRAND_NAME} — top-rated premium essentials. Free shipping on all orders.`
    : `Shop bestsellers from ${BRAND_NAME} — top-rated premium essentials loved by thousands. Free shipping on all orders.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={`${SITE_URL}/og/bestsellers.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={headerLabel} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${SITE_URL}/og/bestsellers.jpg`} />
        <meta name="twitter:image:alt" content={headerLabel} />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <Layout showFooter={true} showBottomTab={true}>
        <main className="bs-page" id="main-content">

          {/* ── Breadcrumb ─────────────────────────────────────────────── */}
          <nav className="bs-breadcrumb" aria-label="Breadcrumb">
            <ol className="bs-breadcrumb__list">
              <li>
                <Link to="/" className="bs-breadcrumb__link">Home</Link>
              </li>
              {/* separator aria-hidden; CSS provides decoration */}
              <li className="bs-breadcrumb__sep" aria-hidden="true" />
              <li>
                <span aria-current="page" className="bs-breadcrumb__current">
                  {headerLabel}
                </span>
              </li>
            </ol>
          </nav>

          {/* ── Page Header ─────────────────────────────────────────────── */}
          <header className="bs-header">
            <h1 className="bs-header__title">{headerLabel}</h1>
            <p className="bs-header__eyebrow" aria-hidden="true">Top Rated</p>
            <p className="bs-header__sub">
              Premium essentials loved by thousands
              {products.length > 0 && (
                <span className="bs-header__count"> — {products.length} {products.length === 1 ? 'style' : 'styles'}</span>
              )}
            </p>
          </header>

          {/* ── States ────────────────────────────────────────────────── */}
          {error && !loading ? (
            <ErrorState message={error} onRetry={handleRetry} />

          ) : loading ? (
            <SkeletonGrid />

          ) : products.length > 0 ? (
            <div key="products-content">
              {/* count styled as social proof */}
              <p className="bs-count" aria-live="polite">
                <strong>{products.length}</strong>{' '}
                {products.length === 1 ? 'bestseller' : 'bestsellers'} in stock
              </p>

              <div
                className="bs-grid"
                role="list"
                aria-label={`${products.length} bestseller products`}
              >
                {products.map((product, idx) => {
                  // stable key from normalised pid with fallback
                  const key = product.pid || `product-${idx}`;
                  return (
                    <div key={key} role="listitem">
                      <ProductCard
                        product={product}
                        onAddToCart={handleAddToCart}
                        // LCP hint: first two cards load eagerly
                        priority={idx < 2}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Back to top on long grids */}
              {products.length >= 12 && (
                <div className="bs-back-top">
                  <button
                    type="button"
                    className="bs-btn bs-btn--ghost bs-btn--sm"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    aria-label="Back to top of page"
                  >
                    ↑ Back to top
                  </button>
                </div>
              )}
            </div>

          ) : (
            <EmptyState category={category} />
          )}

          {/* SEO: contextual internal links + long-tail (Moz/Ahrefs) */}
          <section className="bs-seo" aria-label="Explore collections">
            <h2 className="bs-seo__title">Bestsellers at {BRAND_NAME}</h2>
            <p className="bs-seo__text">
              Shop our most loved premium t-shirts and polos for men and women. Free shipping on all orders.
              Explore <Link to="/men">men&apos;s bestsellers</Link>,{' '}
              <Link to="/women">women&apos;s bestsellers</Link>, or{' '}
              <Link to="/shop">browse all collections</Link>.
            </p>
          </section>

        </main>
      </Layout>
    </>
  );
}

export default Bestsellers;
